import { Business } from "@mui/icons-material";
import { Box, CircularProgress, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";

import { PageHeader } from "~ui/atoms/PageHeader/PageHeader";
import { RenderStates } from "~ui/atoms/RenderStates/RenderStates";
import { SearchField } from "~ui/atoms/SearchField/SearchField";
import { MyOrganizationCard } from "~ui/molecules/MyOrganizationCard/MyOrganizationCard";
import { TwoColumnGrid } from "~ui/molecules/TwoColumnGrid/TwoColumnGrid";
import { type Organization } from "~ui/organisms/Organizations/types";

export interface MyOrganizationsTemplateProps {
  data?: Organization[];
  publicPagesEnabled?: boolean;
  status: Utils.QueryStatus;
  showHeader?: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  /** Bubble IDs where the user has the Admin role. Matching orgs link to the admin portal. */
  adminBubbleIds?: string[];
}

function OrganizationsList({
  organizations,
  publicPagesEnabled,
  adminBubbleIds,
}: {
  organizations: Organization[];
  publicPagesEnabled: boolean;
  adminBubbleIds: string[];
}) {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const adminBubbleIdSet = new Set(adminBubbleIds);

  return (
    <TwoColumnGrid singleColumn={isMobileOrTablet}>
      {organizations.map((organization) => (
        <MyOrganizationCard
          key={organization.id}
          isAdmin={Boolean(organization.bubbleId && adminBubbleIdSet.has(organization.bubbleId))}
          organization={organization}
          publicPagesEnabled={publicPagesEnabled}
        />
      ))}
    </TwoColumnGrid>
  );
}

export const MyOrganizationsTemplate = ({
  adminBubbleIds = [],
  data: organizations,
  publicPagesEnabled = false,
  status,
  showHeader = true,
  searchValue,
  onSearchChange,
}: MyOrganizationsTemplateProps) => {
  return (
    <Box sx={{ maxWidth: "100%", overflowX: "hidden", p: { sm: 2, xs: 1 } }}>
      <Stack spacing={2} sx={{ width: "100%" }}>
        {showHeader && (
          <>
            <PageHeader title="My Organizations" icon={<Business data-variant="page-header" />} />
            <SearchField
              onChange={onSearchChange}
              placeholder="Search organizations..."
              value={searchValue}
              sx={{ mb: 1.5 }}
            />
          </>
        )}
        <RenderStates
          data={organizations}
          empty={<Typography>No organizations found</Typography>}
          error={<Typography color="error">Error loading organizations</Typography>}
          pending={<CircularProgress />}
          status={status}
          success={(orgs) => (
            <OrganizationsList
              adminBubbleIds={adminBubbleIds}
              organizations={orgs}
              publicPagesEnabled={publicPagesEnabled}
            />
          )}
        />
      </Stack>
    </Box>
  );
};
